// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/ui/action_menu/MenuItem.module.css';
import {Slider} from '@app/features/ui/components/Slider';
import {clsx} from 'clsx';
import React, {useCallback, useEffect, useState} from 'react';
import {MenuItem as AriaMenuItem} from 'react-aria-components';

interface MenuItemSliderProps {
	label: string;
	value: number;
	minValue?: number;
	maxValue?: number;
	disabled?: boolean;
	onChange?: (value: number) => void;
	onFormat?: (value: number) => string;
}
export const MenuItemSlider = React.forwardRef<HTMLDivElement, MenuItemSliderProps>(
	({label, value, minValue = 0, maxValue = 100, disabled = false, onChange, onFormat}, forwardedRef) => {
		const [localValue, setLocalValue] = useState(value);
		useEffect(() => {
			setLocalValue(value);
		}, [value]);
		const formattedValue = onFormat ? onFormat(localValue) : `${Math.round(localValue)}%`;
		const handleValueChange = useCallback(
			(newValue: number) => {
				setLocalValue(newValue);
				onChange?.(newValue);
			},
			[onChange],
		);
		const handleValueCommit = useCallback(
			(newValue: number) => {
				onChange?.(newValue);
			},
			[onChange],
		);
		const handleSliderInteraction = useCallback((e: React.SyntheticEvent) => {
			e.stopPropagation();
			e.preventDefault();
		}, []);
		return (
			<AriaMenuItem
				ref={forwardedRef}
				className={clsx(styles.sliderItem, {
					[styles.disabled]: disabled,
				})}
				isDisabled={disabled}
				shouldCloseOnSelect={false}
				textValue={label}
			>
				<div role="presentation" onPointerDown={handleSliderInteraction} style={{width: '100%'}}>
					<div className={styles.sliderHeader}>
						<span className={styles.sliderLabel}>{label}</span>
						<span className={styles.sliderValue}>{formattedValue}</span>
					</div>
					<div className={styles.sliderContainer}>
						<Slider
							defaultValue={localValue}
							factoryDefaultValue={100}
							minValue={minValue}
							maxValue={maxValue}
							disabled={disabled}
							onValueChange={handleValueCommit}
							asValueChanges={handleValueChange}
							mini={true}
							value={localValue}
						/>
					</div>
				</div>
			</AriaMenuItem>
		);
	},
);
MenuItemSlider.displayName = 'MenuItemSlider';
